/**
 * AppSidebar — redesign left rail (shared across Portfolio + property surfaces).
 *
 * Recreates the design handoff rail: logo lockup, a Portfolio nav item (altitude
 * 1, open-actions count badge), a property switcher (popover of properties),
 * grouped nav (Perform / Guest experience / Work) with a CORE pill + Tasks count,
 * and a user chip footer (sign-out preserved as a production requirement).
 *
 * mode="portfolio": Portfolio is active, switcher reads "All properties".
 * mode="property":  a property is selected; group items link into it.
 */

import { useState } from "react"
import { Link, useLocation, useNavigate } from "react-router"
import { useAuth } from "@/lib/auth"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { usePlanContext } from "@/lib/usePlanContext"
import { cn } from "@/lib/utils"
import { TrialSidebarBadge } from "@/components/trial/TrialSidebarBadge"
import { OnboardingChecklist } from "@/components/trial/OnboardingChecklist"
import { useTasks } from "@/pages/tasks/useTasks"
import {
  LayoutGrid,
  Grid2x2,
  ChevronDown,
  Home,
  LineChart,
  Star,
  BookOpen,
  ListChecks,
  Settings,
  X,
  LogOut,
  Lock,
  type LucideIcon,
} from "lucide-react"

interface NavItem {
  label: string
  icon: LucideIcon
  section: string
  core?: boolean
  count?: string
}

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: "Perform",
    items: [
      { label: "Overview", icon: Home, section: "" },
      { label: "Revenue", icon: LineChart, section: "performance" },
      { label: "Reputation", icon: Star, section: "reviews" },
    ],
  },
  {
    label: "Guest experience",
    items: [{ label: "Guest Experience", icon: BookOpen, section: "guest-experience", core: true }],
  },
  {
    label: "Work",
    items: [
      { label: "Tasks", icon: ListChecks, section: "tasks" },
      { label: "Settings", icon: Settings, section: "settings" },
    ],
  },
]

const SWITCHER_AVATARS = [
  "bg-[linear-gradient(150deg,var(--sf-forest),var(--sf-brand))]",
  "bg-clay",
  "bg-sky",
  "bg-brand",
]

interface AppSidebarProps {
  mode: "portfolio" | "property"
  open: boolean
  onClose: () => void
}

export function AppSidebar({ mode, open, onClose }: AppSidebarProps) {
  const { user, properties, activePropertyId, logout } = useAuth()
  const { property, propertyId, isDemo } = usePropertyContext()
  const { plan } = usePlanContext()
  const location = useLocation()
  const navigate = useNavigate()
  const [switcherOpen, setSwitcherOpen] = useState(false)

  const path = location.pathname
  const targetId = mode === "property" ? propertyId : activePropertyId || properties[0]?.id || ""
  const isPortfolioActive = path === "/properties"
  // Portfolio altitude + property switching only matter with 2+ properties.
  const canSwitch = properties.length > 1
  const { counts } = useTasks()
  // Badge value only when there is something open — never render a "0" badge.
  const openBadge = counts.todo > 0 ? String(counts.todo) : null

  const hrefFor = (section: string) =>
    section ? `/properties/${targetId}/${section}` : `/properties/${targetId}`

  const isActive = (section: string) => {
    if (mode !== "property" || !targetId) return false
    const href = hrefFor(section)
    return section === "" ? path === `/properties/${targetId}` : path === href || path.startsWith(href + "/")
  }

  const initials = user?.name
    ? user.name.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() || "?"

  const planLabel = plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : null
  const propCount = properties.length
  const footerMeta = `${planLabel ? `${planLabel} plan · ` : ""}${propCount} propert${propCount === 1 ? "y" : "ies"}`

  const goToProperty = (id: string) => {
    setSwitcherOpen(false)
    onClose()
    navigate(`/properties/${id}`)
  }

  return (
    <aside
      className={cn(
        "flex w-[262px] flex-none flex-col border-r border-border bg-card p-3.5",
        "fixed inset-y-0 left-0 z-50 h-screen transition-transform duration-200 ease-out md:sticky md:top-0 md:z-auto md:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full",
      )}
    >
      {/* Logo lockup + mobile close */}
      <div className="flex items-center gap-2.5 px-1.5 pb-3 pt-1">
        <span className="flex h-[26px] w-[26px] items-center justify-center rounded-[8px] bg-[linear-gradient(150deg,var(--sf-forest),var(--sf-brand))] font-display text-[14px] font-extrabold text-white">
          S
        </span>
        <span className="flex-1 font-display text-[16px] font-bold tracking-[-0.02em] text-foreground">Stayframe</span>
        <button
          type="button"
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-line-soft md:hidden"
          aria-label="Close navigation"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <nav className="-mx-1 flex-1 overflow-y-auto px-1">
        {/* Portfolio (altitude 1) — only with 2+ properties */}
        {canSwitch && (
          <Link
            to="/properties"
            onClick={onClose}
            className={cn(
              "mb-2.5 flex w-full items-center gap-[11px] rounded-[10px] px-[11px] py-2.5 text-[13.5px] font-[550]",
              isPortfolioActive
                ? "bg-brand-soft font-semibold text-brand-strong"
                : "text-ink-2 hover:bg-line-soft",
            )}
          >
            <LayoutGrid className="h-[18px] w-[18px] flex-none" strokeWidth={1.7} />
            <span className="flex-1">Portfolio</span>
            {openBadge && (
              <span
                className={cn(
                  "rounded-[6px] px-1.5 py-px font-mono text-[10.5px] font-semibold",
                  isPortfolioActive ? "bg-white text-rose-strong" : "bg-rose-soft text-rose-strong",
                )}
              >
                {openBadge}
              </span>
            )}
          </Link>
        )}

        {/* Property switcher */}
        <div className="relative mb-3">
          <button
            type="button"
            onClick={() => canSwitch && setSwitcherOpen((v) => !v)}
            disabled={!canSwitch}
            className={cn(
              "flex w-full items-center gap-2.5 rounded-[12px] border border-border bg-card px-2 py-2 text-left transition-colors",
              canSwitch ? "cursor-pointer hover:border-line-2" : "cursor-default",
            )}
          >
            {mode === "property" && property ? (
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-brand-soft font-display text-[12px] font-bold text-brand-strong">
                {property.name?.[0]?.toUpperCase() || "P"}
              </span>
            ) : (
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-line-soft text-muted-foreground">
                <Grid2x2 className="h-4 w-4" strokeWidth={1.8} />
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold text-foreground">
                {mode === "property" && property ? property.name : "All properties"}
              </span>
              <span className="block truncate text-[11px] font-medium text-muted-foreground">
                {mode === "property" && property ? property.address || "Property" : "Viewing portfolio"}
              </span>
            </span>
            {canSwitch && (
              <ChevronDown className="h-[15px] w-[15px] flex-none text-muted-foreground" strokeWidth={1.8} />
            )}
          </button>

          {canSwitch && switcherOpen && (
            <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-40 rounded-[14px] border border-border bg-card p-1.5 shadow-lg">
              {properties.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => goToProperty(p.id)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-[10px] px-2 py-2 text-left hover:bg-line-soft",
                    p.id === targetId && "bg-line-soft",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-7 w-7 flex-none items-center justify-center rounded-[8px] font-display text-[11px] font-bold text-white",
                      SWITCHER_AVATARS[i % SWITCHER_AVATARS.length],
                    )}
                  >
                    {p.name?.[0]?.toUpperCase() || "P"}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] font-semibold text-foreground">{p.name}</span>
                    {p.address && (
                      <span className="block truncate text-[10.5px] font-medium text-muted-foreground">{p.address}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Trial status (production requirement — property mode, non-demo) */}
        {mode === "property" && !isDemo && (
          <div className="mb-1">
            <TrialSidebarBadge />
          </div>
        )}

        {/* Grouped nav */}
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <div className="px-[11px] pb-[7px] pt-3.5 text-[10px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
              {group.label}
            </div>
            {group.items.map((item) => {
              const active = isActive(item.section)
              return (
                <Link
                  key={item.label}
                  to={hrefFor(item.section)}
                  onClick={onClose}
                  className={cn(
                    "mb-0.5 flex w-full items-center gap-[11px] rounded-[10px] px-[11px] py-2 text-[13.5px] font-[550]",
                    active ? "bg-brand-soft font-semibold text-brand-strong" : "text-ink-2 hover:bg-line-soft",
                  )}
                >
                  <item.icon className="h-[18px] w-[18px] flex-none" strokeWidth={1.7} />
                  <span className="flex-1">{item.label}</span>
                  {item.core && (
                    <span className="rounded-[6px] bg-brand-soft px-1.5 py-0.5 text-[9px] font-semibold tracking-[0.04em] text-brand-strong">
                      CORE
                    </span>
                  )}
                  {isDemo && item.section === "settings" && (
                    <Lock className="h-3 w-3 flex-none text-muted-foreground/50" strokeWidth={1.8} />
                  )}
                  {(item.section === "tasks" ? openBadge : item.count) && (
                    <span className="rounded-[6px] bg-line-soft px-1.5 py-px font-mono text-[10.5px] font-semibold text-ink-2">
                      {item.section === "tasks" ? openBadge : item.count}
                    </span>
                  )}
                </Link>
              )
            })}
          </div>
        ))}
      </nav>

      {/* Onboarding checklist (property mode only — will move to Settings > Setup later) */}
      {mode === "property" && <OnboardingChecklist />}

      {/* User chip footer */}
      <div className="mt-2.5 flex items-center gap-2.5 rounded-[14px] border border-border bg-line-soft px-3 py-2.5">
        <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-full bg-[#D8E8E0] font-display text-[12px] font-bold text-brand-strong">
          {initials}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12px] font-semibold text-foreground">{user?.name || user?.email || ""}</span>
          <span className="block truncate text-[10.5px] font-medium text-muted-foreground">{footerMeta}</span>
        </span>
        <button
          type="button"
          onClick={logout}
          className="flex h-7 w-7 flex-none items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white hover:text-rose-strong"
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="h-4 w-4" strokeWidth={1.7} />
        </button>
      </div>
    </aside>
  )
}
